/**
 * Content Sets Page
 *
 * Manages user and organization content sets for RAG-enhanced practice.
 */

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useListContentSetsQuery,
  useCreateContentSetMutation,
  useDeleteContentSetMutation,
  useGetContentSetQuery,
  useAddContentItemMutation,
  useRemoveContentItemMutation,
  useGetContentSetIndexStatusQuery,
  useReindexContentSetMutation,
  useUploadContentFileMutation,
  type ContentItem,
  type ContentItemType,
  type EmbeddingStatus,
} from '../store/api/trainingApi';
import type { RootState } from '../store';
import { useGetCapabilitiesQuery } from '../store/api/capabilitiesApi';
import { selectTokenWorkspaceId } from '../store/slices/centralAuthSlice';
import { mutationOutcome } from '../utils/mutationOutcome';

/**
 * The content library feeds semantic retrieval. Until the server reports a real
 * embeddings provider + persistent vector store (GET /capabilities rag: true)
 * nothing can be indexed or searched, so the page says so and offers no
 * create / add / upload / reindex controls. Existing sets can still be viewed
 * and deleted.
 */
function useContentLibraryAvailable(): boolean {
  const { data, isSuccess } = useGetCapabilitiesQuery();
  return isSuccess && data?.rag === true;
}

const LibraryUnavailableNotice: React.FC = () => (
  <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4" role="status" data-testid="content-library-unavailable">
    <h2 className="text-sm font-medium text-gray-900">Content Library is not available yet</h2>
    <p className="mt-1 text-sm text-gray-600">
      Semantic search over your content is not set up on this server, so content cannot be indexed or used
      in practice feedback. You can still view and delete existing content sets.
    </p>
  </div>
);

// ============================================
// Status Badge Component
// ============================================

const StatusBadge: React.FC<{ status: EmbeddingStatus; retrievalUnavailable?: boolean }> = ({
  status,
  retrievalUnavailable,
}) => {
  // Without a real embeddings provider + vector store nothing is indexed, and
  // a "Pending" badge would promise indexing that is not going to happen (and
  // an "Indexed" left over from the old hash-vector stub would be false).
  if (retrievalUnavailable) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
        Not indexed
      </span>
    );
  }

  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    indexed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
  };

  const labels = {
    pending: 'Pending',
    indexed: 'Indexed',
    failed: 'Failed',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
      {labels[status]}
    </span>
  );
};

// ============================================
// Content Item Row Component
// ============================================

interface ContentItemRowProps {
  item: ContentItem;
  onRemove: () => void;
  retrievalUnavailable?: boolean;
}

const ContentItemRow: React.FC<ContentItemRowProps> = ({ item, onRemove, retrievalUnavailable }) => {
  const typeIcons = {
    file: '📄',
    link: '🔗',
    note: '📝',
  };

  return (
    <div className="flex items-center justify-between py-3 px-4 border-b border-gray-100 hover:bg-gray-50">
      <div className="flex items-center gap-3">
        <span className="text-lg">{typeIcons[item.type]}</span>
        <div>
          <p className="font-medium text-gray-900">{item.title}</p>
          <p className="text-sm text-gray-500 truncate max-w-md">
            {item.storagePathOrUrl}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <StatusBadge status={item.embeddingStatus} retrievalUnavailable={retrievalUnavailable} />
        <button
          onClick={onRemove}
          className="text-red-500 hover:text-red-700 text-sm"
        >
          Remove
        </button>
      </div>
    </div>
  );
};

// ============================================
// Add Item Form Component
// ============================================

interface AddItemFormProps {
  contentSetId: string;
  onClose: () => void;
}

const AddItemForm: React.FC<AddItemFormProps> = ({ contentSetId, onClose }) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState<ContentItemType>('note');
  const [content, setContent] = useState('');
  const [addItem, { isLoading }] = useAddContentItemMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addItem({
        contentSetId,
        title,
        type,
        storagePathOrUrl: content,
        metadata: type === 'note' ? { text: content } : undefined,
      }).unwrap();
      onClose();
    } catch (error) {
      console.error('Failed to add item:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-gray-50 rounded-lg">
      <div className="grid gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter title..."
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as ContentItemType)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="note">Note (Text)</option>
            <option value="link">Link (URL)</option>
            <option value="file">File (URL/Path)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {type === 'note' ? 'Content' : 'URL'}
          </label>
          {type === 'note' ? (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows={4}
              placeholder="Enter your notes..."
              required
            />
          ) : (
            <input
              type="url"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://..."
              required
            />
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Adding...' : 'Add Item'}
          </button>
        </div>
      </div>
    </form>
  );
};

// ============================================
// File Upload Form Component
// ============================================

interface FileUploadFormProps {
  contentSetId: string;
  onClose: () => void;
}

const FileUploadForm: React.FC<FileUploadFormProps> = ({ contentSetId, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadFile, { isLoading, isError, error }] = useUploadContentFileMutation();
  const [uploadResult, setUploadResult] = useState<{
    filename: string;
    textExtracted: boolean;
    chunksIndexed: number;
  } | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setUploadResult(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;

    try {
      const result = await uploadFile({ contentSetId, file }).unwrap();
      setUploadResult(result);
      setFile(null);
      // Reset the file input
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (err) {
      console.error('Failed to upload file:', err);
    }
  };

  const acceptedTypes = '.pdf,.docx,.doc,.txt,.md,.csv';

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-blue-50 rounded-lg mb-4">
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload Document
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Supported formats: PDF, DOCX, TXT, MD, CSV (max 50MB)
        </p>
        <input
          id="file-upload"
          type="file"
          accept={acceptedTypes}
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700"
        />
      </div>

      {file && (
        <div className="flex items-center justify-between bg-white p-3 rounded-md mb-3">
          <div>
            <p className="font-medium text-gray-900">{file.name}</p>
            <p className="text-sm text-gray-500">
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
          <button
            type="button"
            onClick={() => setFile(null)}
            className="text-red-500 hover:text-red-700"
          >
            Remove
          </button>
        </div>
      )}

      {isError && (
        <div className="mb-3 p-3 bg-red-100 text-red-700 rounded-md text-sm">
          Failed to upload file. {(error as any)?.data?.error || 'Please try again.'}
        </div>
      )}

      {uploadResult && (
        <div className="mb-3 p-3 bg-green-100 text-green-700 rounded-md text-sm">
          <p className="font-medium">Uploaded: {uploadResult.filename}</p>
          <p>
            Text extracted: {uploadResult.textExtracted ? 'Yes' : 'No'} |
            Chunks indexed: {uploadResult.chunksIndexed}
          </p>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
        >
          Close
        </button>
        <button
          type="submit"
          disabled={!file || isLoading}
          className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Uploading...' : 'Upload File'}
        </button>
      </div>
    </form>
  );
};

// ============================================
// Content Set Detail Component
// ============================================

interface ContentSetDetailProps {
  contentSetId: string;
  onBack: () => void;
}

const ContentSetDetail: React.FC<ContentSetDetailProps> = ({ contentSetId, onBack }) => {
  const libraryAvailable = useContentLibraryAvailable();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const { data: contentSet, isLoading } = useGetContentSetQuery(contentSetId);
  const { data: indexStatus } = useGetContentSetIndexStatusQuery(contentSetId);
  const [removeItem] = useRemoveContentItemMutation();
  const [reindex, { isLoading: isReindexing }] = useReindexContentSetMutation();
  /** A remove / reindex the server refused (403, 503 …): told, never assumed done. */
  const [mutationError, setMutationError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!contentSet) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Content set not found</p>
        <button onClick={onBack} className="mt-4 text-blue-600 hover:text-blue-800">
          Go back
        </button>
      </div>
    );
  }

  const handleRemoveItem = async (itemId: string) => {
    if (confirm('Are you sure you want to remove this item?')) {
      setMutationError(null);
      await mutationOutcome(
        removeItem({ contentSetId, itemId }),
        'The item could not be removed. It is still in this content set.',
        setMutationError
      );
    }
  };

  const handleReindex = async () => {
    setMutationError(null);
    await mutationOutcome(reindex(contentSetId), 'Reindexing could not be started. Nothing was reindexed.', setMutationError);
  };

  const retrievalUnavailable = indexStatus?.retrievalAvailable === false || !libraryAvailable;

  return (
    <div>
      {mutationError && (
        <p className="mb-4 text-sm text-red-600" role="alert" data-testid="content-set-mutation-error">{mutationError}</p>
      )}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700"
          >
            ← Back
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{contentSet.name}</h2>
            {contentSet.description && (
              <p className="text-gray-500">{contentSet.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          {retrievalUnavailable ? (
            <div className="text-sm text-gray-600" role="status">
              Indexing and semantic search are not available on this server.
            </div>
          ) : indexStatus && (
            <div className="text-sm text-gray-500">
              {indexStatus.indexed}/{indexStatus.total} indexed
              {indexStatus.pending > 0 && ` (${indexStatus.pending} pending)`}
              {indexStatus.failed > 0 && (
                <span className="text-red-500 ml-1">({indexStatus.failed} failed)</span>
              )}
            </div>
          )}
          {!retrievalUnavailable && (
            <button
              onClick={handleReindex}
              disabled={isReindexing}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {isReindexing ? 'Reindexing...' : 'Reindex All'}
            </button>
          )}
        </div>
      </div>

      {showUploadForm && (
        <FileUploadForm contentSetId={contentSetId} onClose={() => setShowUploadForm(false)} />
      )}

      {retrievalUnavailable ? null : showAddForm ? (
        <AddItemForm contentSetId={contentSetId} onClose={() => setShowAddForm(false)} />
      ) : (
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setShowUploadForm(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Upload File
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            + Add Note/Link
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200">
        {contentSet.items.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No content items yet. Add your first item above.
          </div>
        ) : (
          contentSet.items.map((item) => (
            <ContentItemRow
              key={item.id}
              item={item}
              onRemove={() => handleRemoveItem(item.id)}
              retrievalUnavailable={retrievalUnavailable}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ============================================
// Create Content Set Form
// ============================================

interface CreateContentSetFormProps {
  ownerType: 'user' | 'org';
  ownerId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateContentSetForm: React.FC<CreateContentSetFormProps> = ({
  ownerType,
  ownerId,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [createContentSet, { isLoading }] = useCreateContentSetMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createContentSet({
        ownerType,
        ownerId,
        name,
        description: description || undefined,
      }).unwrap();
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to create content set:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-gray-50 rounded-lg mb-4">
      <div className="grid gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., Product Knowledge Base"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (optional)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            rows={2}
            placeholder="What content will this collection contain?"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </form>
  );
};

// ============================================
// Content Sets List Component
// ============================================

interface ContentSetsListProps {
  ownerType: 'user' | 'org';
  ownerId: string;
  onSelectContentSet: (id: string) => void;
}

const ContentSetsList: React.FC<ContentSetsListProps> = ({
  ownerType,
  ownerId,
  onSelectContentSet,
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const libraryAvailable = useContentLibraryAvailable();
  const { data: contentSets, isLoading, refetch } = useListContentSetsQuery({ ownerType, ownerId });
  const [deleteContentSet] = useDeleteContentSetMutation();
  /** A delete the server refused: the set still exists, and the user is told. */
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = async (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete "${name}"? This will also remove all indexed content.`)) {
      setDeleteError(null);
      await mutationOutcome(deleteContentSet(id), `"${name}" could not be deleted. It still exists.`, setDeleteError);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      {deleteError && (
        <p className="mb-4 text-sm text-red-600" role="alert" data-testid="content-set-delete-error">{deleteError}</p>
      )}
      {!libraryAvailable ? null : showCreateForm ? (
        <CreateContentSetForm
          ownerType={ownerType}
          ownerId={ownerId}
          onClose={() => setShowCreateForm(false)}
          onSuccess={refetch}
        />
      ) : (
        <button
          onClick={() => setShowCreateForm(true)}
          className="mb-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          + New Content Set
        </button>
      )}

      {(!contentSets || contentSets.length === 0) ? (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
          <p>No content sets yet.</p>
          {libraryAvailable && (
            <p className="text-sm mt-1">Create one to start adding content for AI-enhanced practice.</p>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {contentSets.map((cs) => (
            <div
              key={cs.id}
              className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div
                  className="flex-1 cursor-pointer"
                  onClick={() => onSelectContentSet(cs.id)}
                >
                  <h3 className="font-medium text-gray-900">{cs.name}</h3>
                  {cs.description && (
                    <p className="text-sm text-gray-500 mt-1">{cs.description}</p>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(cs.id, cs.name)}
                  className="ml-4 text-red-500 hover:text-red-700 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// Main Page Component
// ============================================

const ContentSetsPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.centralAuth.user);
  // Workspace content belongs to the workspace in the signed-in token — the
  // server authorizes on it (Central training:read / training:write), never on
  // a local organization membership.
  const workspaceId = useSelector(selectTokenWorkspaceId);
  const libraryAvailable = useContentLibraryAvailable();
  const [activeTab, setActiveTab] = useState<'my' | 'org'>('my');
  const [selectedContentSetId, setSelectedContentSetId] = useState<string | null>(null);

  if (!user) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Please log in to manage content.</p>
      </div>
    );
  }

  // If viewing a specific content set
  if (selectedContentSetId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <ContentSetDetail
          contentSetId={selectedContentSetId}
          onBack={() => setSelectedContentSetId(null)}
        />
      </div>
    );
  }

  const hasWorkspace = !!workspaceId;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Content Library</h1>
        <p className="text-gray-500 mt-1">
          Manage content sets for AI-enhanced practice sessions
        </p>
      </div>

      {!libraryAvailable && <LibraryUnavailableNotice />}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('my')}
          className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px ${
            activeTab === 'my'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          My Content
        </button>
        {hasWorkspace && (
          <button
            onClick={() => setActiveTab('org')}
            className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px ${
              activeTab === 'org'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Workspace Content
          </button>
        )}
      </div>

      {/* Content Lists */}
      {activeTab === 'my' ? (
        <ContentSetsList
          ownerType="user"
          ownerId={user.id}
          onSelectContentSet={setSelectedContentSetId}
        />
      ) : workspaceId ? (
        <ContentSetsList
          ownerType="org"
          ownerId={workspaceId}
          onSelectContentSet={setSelectedContentSetId}
        />
      ) : (
        <div className="text-center py-8 text-gray-500">
          No workspace in your session.
        </div>
      )}
    </div>
  );
};

export default ContentSetsPage;
